import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";

type OrgSportsMapping = Record<string, string>; // { "judo": "Judo" }

type OrgData = {
  org_id: string;
  org_name: string;
  sports: OrgSportsMapping;
  clubs_enabled: boolean;
};

type VenueData = {
  venue_name: string;
  sport?: string;
  geolocation?: { lat: number; lng: number } | null;
  org?: OrgData | null;
};

type AnonymousSession = {
  sessionId: string;
  venueId: string | null;
  venueData: VenueData | null;
  isAnonymous: boolean;
  startedAt: number;
  userGeolocation: { lat: number; lng: number } | null;
  // Supabase anonymous auth user ID — populated after signInAnonymously() succeeds
  anonUserId: string | null;
};

type AnonymousSessionContextType = AnonymousSession & {
  setVenueId: (venueId: string) => void;
  setVenueData: (data: VenueData) => void;
  setUserGeolocation: (coords: { lat: number; lng: number }) => void;
  clearSession: () => void;
  anonUserId: string | null;
};

const AnonymousSessionContext = createContext<AnonymousSessionContextType | null>(null);

const SESSION_KEY = "charlie_anonymous_session";
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const createFreshSession = (): AnonymousSession => ({
  sessionId: crypto.randomUUID(),
  venueId: null,
  venueData: null,
  isAnonymous: true,
  startedAt: Date.now(),
  userGeolocation: null,
  anonUserId: null,
});

export const AnonymousSessionProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AnonymousSession>(() => {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const parsed: AnonymousSession = JSON.parse(stored);
        // Check 24h TTL — create fresh session if expired
        if (Date.now() - parsed.startedAt > TTL_MS) {
          return createFreshSession();
        }
        return parsed;
      } catch {
        // Ignore invalid stored data
      }
    }
    return createFreshSession();
  });

  useEffect(() => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }, [session]);

  // Sign in anonymously with Supabase so QR users get a real auth user_id in incident logs
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (existingSession) return; // Logged-in user — skip anonymous sign-in
      supabase.auth.signInAnonymously().then(({ data, error }) => {
        if (error) {
          console.warn("[AnonymousSession] signInAnonymously failed:", error.message);
          return; // Non-blocking — assessment still works without anonUserId
        }
        if (data.user) {
          setSession((prev) => ({ ...prev, anonUserId: data.user!.id }));
        }
      });
    });
  }, []);

  const setVenueId = useCallback((venueId: string) => {
    setSession((prev) => ({ ...prev, venueId }));
  }, []);

  const setVenueData = useCallback((data: VenueData) => {
    setSession((prev) => ({ ...prev, venueData: data }));
  }, []);

  const setUserGeolocation = useCallback((coords: { lat: number; lng: number }) => {
    setSession((prev) => ({ ...prev, userGeolocation: coords }));
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setSession(createFreshSession());
  }, []);

  return (
    <AnonymousSessionContext.Provider
      value={{ ...session, setVenueId, setVenueData, setUserGeolocation, clearSession }}
    >
      {children}
    </AnonymousSessionContext.Provider>
  );
};

export const useAnonymousSession = () => {
  const context = useContext(AnonymousSessionContext);
  if (!context) {
    throw new Error("useAnonymousSession must be used within AnonymousSessionProvider");
  }
  return context;
};

// Returns null instead of throwing when outside provider
export const useAnonymousSessionOptional = () => {
  return useContext(AnonymousSessionContext);
};
